import { useEffect } from 'react';
import { useSelector } from 'react-redux';

/**
 * ThemeProvider, makes `businessInfo` design tokens LIVE.
 * ────────────────────────────────────────────────────────
 * The Tailwind palette reads RGB-triplet CSS vars (see tailwind.config.js), with
 * defaults in index.css. When the dealer's system_data carries theme colors/radius/
 * font, this derives the full 50-950 scales from the base hexes and writes the vars
 * onto :root at runtime, recoloring the whole site without a rebuild. No tokens →
 * it does nothing and the curated defaults stand.
 *
 * The hub (flowma ThemeService) only needs to set two base colors, `colors.primary`
 * (structure) and `colors.secondary` (accent/CTA), plus optional radius + font;
 * every shade is derived here so a dealer's pick can't produce a broken scale.
 */

// ── Pure color helpers ───────────────────────────────────────────────────────
const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

const hexToRgb = (hex) => {
    let h = String(hex || '').trim().replace(/^#/, '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const mix = (rgb, target, amt) => rgb.map((c, i) => Math.round(c + (target[i] - c) * amt));
const triplet = (rgb) => rgb.join(' ');

// 50-950 scale from a base hex at 500 (tint toward white, shade toward black).
const SHADES = { 50: 0.92, 100: 0.84, 200: 0.68, 300: 0.48, 400: 0.26, 600: -0.12, 700: -0.28, 800: -0.44, 900: -0.60, 950: -0.74 };
const scaleFromBase = (baseHex) => {
    const b = hexToRgb(baseHex);
    if (!b) return null;
    const out = { 500: b };
    for (const [shade, amt] of Object.entries(SHADES)) out[shade] = mix(b, amt > 0 ? WHITE : BLACK, Math.abs(amt));
    return out;
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function ThemeProvider({ children = null }) {
    const raw = useSelector((s) => s.systemData?.businessInfo);
    const bi = Array.isArray(raw) ? (raw[0] || {}) : (raw || {});

    useEffect(() => {
        const root = document.documentElement.style;
        const colors = bi.colors || {};

        // [1] Primary (structure) → primary scale + the navy dark family.
        if (colors.primary) {
            const s = scaleFromBase(colors.primary);
            if (s) {
                Object.entries(s).forEach(([k, rgb]) => root.setProperty(`--c-primary-${k}`, triplet(rgb)));
                const b = hexToRgb(colors.primary);
                root.setProperty('--c-navy', triplet(b));
                root.setProperty('--c-navy-card', triplet(mix(b, WHITE, 0.10)));
                root.setProperty('--c-navy-line', triplet(mix(b, WHITE, 0.22)));
                root.setProperty('--c-navy-deep', triplet(mix(b, BLACK, 0.30)));
            }
        }

        // [2] Secondary (accent / CTA) → secondary + accent scales + brand + input focus.
        if (colors.secondary) {
            const s = scaleFromBase(colors.secondary);
            if (s) {
                Object.entries(s).forEach(([k, rgb]) => {
                    root.setProperty(`--c-secondary-${k}`, triplet(rgb));
                    root.setProperty(`--c-accent-${k}`, triplet(rgb));
                });
                const b = hexToRgb(colors.secondary);
                root.setProperty('--c-brand', triplet(b));
                root.setProperty('--c-brand-soft', triplet(mix(b, WHITE, 0.85)));
                root.setProperty('--input-focus', triplet(b));
            }
        }

        // [3] Radius, card/field (buttons.radius as a fallback for fields).
        const r = bi.radius || {};
        const btn = bi.buttons || {};
        if (r.card) root.setProperty('--radius-card', r.card);
        if (r.field || btn.radius) root.setProperty('--radius-field', r.field || btn.radius);

        // [4] Font, set the CSS-var stacks + inject the Google-Fonts link once.
        const f = bi.font || {};
        const headingStack = f.heading?.stack || f.stack;
        const bodyStack = f.body?.stack || f.stack;
        if (headingStack) root.setProperty('--font-secondary', headingStack);
        if (bodyStack) root.setProperty('--font-primary', bodyStack);
        if (f.googleFontsUrl) injectFontLink(f.googleFontsUrl);
    }, [bi]);

    return children;
}

// Append (or update) a single <link> for the theme's Google-Fonts URL.
function injectFontLink(url) {
    if (typeof document === 'undefined' || !url) return;
    let link = document.getElementById('theme-font');
    if (!link) {
        link = document.createElement('link');
        link.id = 'theme-font';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
}
